import { useState, useEffect } from 'react';
import { aquaApi } from '../aquaApi';
import { fmtDate } from '../dateUtils';
import DateInput from '../DateInput';

const fmt  = (v) => v != null && v !== '' ? Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
const fmtQ = (v) => v != null && v !== '' ? parseFloat(v).toFixed(3) : '0.000';

const UOMS   = ['Nos', 'Lsum', 'KGs', 'Bags', 'Ltrs'];
const PONDS  = ['P1','P2','P3','P4','P5','P6','P7','P8'];

// ── summary card ─────────────────────────────────────────────────────────────

function SummaryCard({ label, value, sub, color, icon, bg }) {
  return (
    <div className="stat-card" style={bg ? { borderLeft: `4px solid ${color}` } : {}}>
      <div className="stat-icon" style={{ background: bg || '#e8f0fe' }}>
        <i className={`fas ${icon}`} style={{ color: color || '#1a73e8' }} />
      </div>
      <div>
        <div className="stat-label">{label}</div>
        <div className="stat-value" style={{ color: color || '#202124' }}>₹{fmt(value)}</div>
        {sub && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

// ── Expenses tab ─────────────────────────────────────────────────────────────

const EXP_EMPTY = { cropId: '', expenseDate: new Date().toISOString().split('T')[0], eventLabel: '', description: '', uom: 'Nos', nou: '', rpu: '', amount: '', notes: '' };

function ExpensesTab({ cropId, activeCrops, canWrite, canDelete }) {
  const [rows, setRows]   = useState([]);
  const [form, setForm]   = useState({ ...EXP_EMPTY, cropId: cropId || '' });
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [err, setErr]   = useState('');

  const load = () => {
    if (!form.cropId && !cropId) return;
    setLoading(true);
    aquaApi.getHarvestExpenses(form.cropId || cropId).then(setRows).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [form.cropId, cropId]);

  const set = (k, v) => setForm(f => {
    const next = { ...f, [k]: v };
    if (k === 'nou' || k === 'rpu') {
      const nou = parseFloat(k === 'nou' ? v : next.nou) || 0;
      const rpu = parseFloat(k === 'rpu' ? v : next.rpu) || 0;
      if (next.uom !== 'Lsum') next.amount = (nou * rpu).toFixed(2);
    }
    return next;
  });

  const handleSave = async () => {
    if (!form.cropId) { setErr('Select a crop'); return; }
    if (!form.description.trim()) { setErr('Description is required'); return; }
    try {
      if (editId) await aquaApi.updateHarvestExpense(editId, form);
      else await aquaApi.createHarvestExpense(form);
      setForm({ ...EXP_EMPTY, cropId: form.cropId }); setEditId(null); setErr(''); setShowForm(false); load();
    } catch (e) { setErr(e.message); }
  };

  const handleEdit = (r) => {
    setForm({ cropId: r.crop_id, expenseDate: r.expense_date || '', eventLabel: r.event_label || '',
      description: r.description || '', uom: r.uom || 'Nos',
      nou: r.nou || '', rpu: r.rpu || '', amount: r.amount || '', notes: r.notes || '' });
    setEditId(r.id); setShowForm(true);
  };

  // Group by event_label + date for display
  const grandTotal = rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);
  const groups = rows.reduce((acc, r) => {
    const key = (r.expense_date || '') + '|' + (r.event_label || '');
    if (!acc[key]) acc[key] = { date: r.expense_date, label: r.event_label, items: [] };
    acc[key].items.push(r);
    return acc;
  }, {});

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: '#5f6368' }}>🦐 Crop:</span>
          <select className="form-control" style={{ width: 180, padding: '4px 8px' }}
            value={form.cropId} onChange={e => setForm(f => ({ ...f, cropId: e.target.value }))}>
            <option value="">— Select Crop —</option>
            {activeCrops.slice().sort((a, b) => a.crop_code.localeCompare(b.crop_code)).map(c => <option key={c.id} value={c.id}>{c.crop_code}</option>)}
          </select>
        </div>
        {canWrite && (
          <button className="btn btn-primary" style={{ padding: '5px 14px', fontSize: 13 }}
            onClick={() => { setShowForm(s => !s); setEditId(null); setForm(f => ({ ...EXP_EMPTY, cropId: f.cropId })); }}>
            {showForm ? '✕ Close' : '➕ Add Expense'}
          </button>
        )}
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><div className="card-title">{editId ? '✏️ Edit Expense' : '➕ Add Harvest Expense'}</div></div>
          <div className="card-body">
            {err && <div className="alert alert-error" style={{ marginBottom: 10 }}>{err}</div>}
            <div className="form-grid cols-3">
              <div className="form-group">
                <label className="form-label">Date</label>
                <DateInput className="form-control" value={form.expenseDate} onChange={e => set('expenseDate', e.target.value)} />
              </div>
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label className="form-label">Event Label (e.g. HARVESTING: P1 & P2)</label>
                <input className="form-control" placeholder="HARVESTING: P1 & P2, CLEARENCEs: P2…"
                  value={form.eventLabel} onChange={e => set('eventLabel', e.target.value)} />
              </div>
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label className="form-label">Description *</label>
                <input className="form-control" placeholder="SRIRAMULU_LABOUR_(15+1), VALA_RENT…"
                  value={form.description} onChange={e => set('description', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">UOM</label>
                <select className="form-control" value={form.uom} onChange={e => set('uom', e.target.value)}>
                  {UOMS.map(u => <option key={u}>{u}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">NOU (Qty)</label>
                <input className="form-control" type="number" step="0.01" value={form.nou} onChange={e => set('nou', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">RPU (Rate)</label>
                <input className="form-control" type="number" step="0.01" value={form.rpu} onChange={e => set('rpu', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Amount (₹) {form.uom !== 'Lsum' ? '← auto' : ''}</label>
                <input className="form-control" type="number" step="0.01" value={form.amount} onChange={e => set('amount', e.target.value)}
                  style={{ fontWeight: 700, color: '#c5221f' }} />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Notes</label>
                <input className="form-control" value={form.notes} onChange={e => set('notes', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button className="btn btn-primary" onClick={handleSave}>{editId ? 'Update' : 'Save'}</button>
              <button className="btn btn-secondary" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <div className="loading-msg">Loading…</div> : (
        <div className="card">
          <div className="card-header">
            <div className="card-title">💸 Harvest Expenses</div>
            <div style={{ fontWeight: 700, color: '#c5221f', fontSize: 15 }}>Total: ₹{fmt(grandTotal)}</div>
          </div>
          <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr style={{ background: '#7c3aed', color: '#fff' }}>
                  <th style={{ color: '#fff' }}>DATE</th>
                  <th style={{ color: '#fff' }}>DESCRIPTION</th>
                  <th style={{ color: '#fff', textAlign: 'center' }}>UOM</th>
                  <th style={{ color: '#fff', textAlign: 'right' }}>NOU</th>
                  <th style={{ color: '#fff', textAlign: 'right' }}>RPU</th>
                  <th style={{ color: '#fff', textAlign: 'right' }}>XPENSEs (₹)</th>
                  <th style={{ color: '#fff' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888', padding: 32 }}>No expenses recorded</td></tr>}
                {Object.values(groups).map((g, gi) => (
                  g.items.map((r, i) => (
                    <tr key={r.id}>
                      {i === 0 && <td rowSpan={g.items.length} style={{ fontWeight: 700, whiteSpace: 'nowrap', verticalAlign: 'top', padding: '10px 12px' }}>{fmtDate(r.expense_date)}</td>}
                      <td>
                        {i === 0 && g.label && (
                          <div style={{ fontWeight: 800, fontSize: 13, color: '#7c3aed', marginBottom: 2 }}>{g.label}</div>
                        )}
                        <div style={{ textAlign: 'center', fontSize: 13 }}>{r.description}</div>
                      </td>
                      <td style={{ textAlign: 'center' }}>{r.uom}</td>
                      <td style={{ textAlign: 'right' }}>{fmtQ(r.nou)}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(r.rpu)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>₹{fmt(r.amount)}</td>
                      <td>
                        {canWrite && <button className="btn-sm btn-secondary" onClick={() => handleEdit(r)} style={{ marginRight: 4 }}>Edit</button>}
                        {canDelete && <button className="btn-sm btn-danger" onClick={async () => { if (window.confirm('Delete?')) { await aquaApi.deleteHarvestExpense(r.id); load(); } }}>Del</button>}
                      </td>
                    </tr>
                  ))
                ))}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr style={{ background: '#f3e8fd' }}>
                    <td colSpan={5} style={{ fontWeight: 800, padding: '10px 12px', textAlign: 'right', color: '#7c3aed' }}>TOTAL HARVEST EXPENSES</td>
                    <td style={{ textAlign: 'right', fontWeight: 800, padding: '10px 12px', color: '#c5221f', fontSize: 15 }}>₹{fmt(grandTotal)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sales tab ─────────────────────────────────────────────────────────────────

const SALE_EMPTY = { cropId: '', saleDate: new Date().toISOString().split('T')[0], pondNo: 'P1', doc: '', buyer: '', cnt: '', rpu: '', cm: '0', uom: 'Kgs', nou: '', tRate: '', amount: '', notes: '' };

function SalesTab({ cropId, activeCrops, canWrite, canDelete }) {
  const [rows, setRows]   = useState([]);
  const [form, setForm]   = useState({ ...SALE_EMPTY, cropId: cropId || '' });
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [err, setErr]   = useState('');

  const load = () => {
    if (!form.cropId && !cropId) return;
    setLoading(true);
    aquaApi.getHarvestSales(form.cropId || cropId).then(setRows).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [form.cropId, cropId]);

  const set = (k, v) => setForm(f => {
    const next = { ...f, [k]: v };
    if (k === 'rpu' || k === 'cm') {
      const rpu = parseFloat(k === 'rpu' ? v : next.rpu) || 0;
      const cm  = parseFloat(k === 'cm'  ? v : next.cm)  || 0;
      next.tRate = (rpu - cm).toFixed(2);
    }
    if (k === 'nou' || k === 'tRate' || k === 'rpu' || k === 'cm') {
      const nou   = parseFloat(k === 'nou' ? v : next.nou) || 0;
      const tRate = parseFloat(next.tRate) || 0;
      next.amount = (nou * tRate).toFixed(2);
    }
    return next;
  });

  const handleSave = async () => {
    if (!form.cropId) { setErr('Select a crop'); return; }
    if (!form.buyer.trim()) { setErr('Buyer is required'); return; }
    try {
      if (editId) await aquaApi.updateHarvestSale(editId, form);
      else await aquaApi.createHarvestSale(form);
      setForm({ ...SALE_EMPTY, cropId: form.cropId }); setEditId(null); setErr(''); setShowForm(false); load();
    } catch (e) { setErr(e.message); }
  };

  const handleEdit = (r) => {
    setForm({ cropId: r.crop_id, saleDate: r.sale_date || '', pondNo: r.pond_no || 'P1',
      doc: r.doc || '', buyer: r.buyer || '', cnt: r.cnt || '',
      rpu: r.rpu || '', cm: r.cm || '0', uom: r.uom || 'Kgs',
      nou: r.nou || '', tRate: r.t_rate || '', amount: r.amount || '', notes: r.notes || '' });
    setEditId(r.id); setShowForm(true);
  };

  const totalKg  = rows.reduce((s, r) => s + parseFloat(r.nou || 0), 0);
  const totalAmt = rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: '#5f6368' }}>🦐 Crop:</span>
          <select className="form-control" style={{ width: 180, padding: '4px 8px' }}
            value={form.cropId} onChange={e => setForm(f => ({ ...f, cropId: e.target.value }))}>
            <option value="">— Select Crop —</option>
            {activeCrops.slice().sort((a, b) => a.crop_code.localeCompare(b.crop_code)).map(c => <option key={c.id} value={c.id}>{c.crop_code}</option>)}
          </select>
        </div>
        {canWrite && (
          <button className="btn btn-primary" style={{ padding: '5px 14px', fontSize: 13 }}
            onClick={() => { setShowForm(s => !s); setEditId(null); setForm(f => ({ ...SALE_EMPTY, cropId: f.cropId })); }}>
            {showForm ? '✕ Close' : '➕ Add Sale'}
          </button>
        )}
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><div className="card-title">{editId ? '✏️ Edit Sale' : '➕ Add Harvest Sale'}</div></div>
          <div className="card-body">
            {err && <div className="alert alert-error" style={{ marginBottom: 10 }}>{err}</div>}
            <div className="form-grid cols-4">
              <div className="form-group">
                <label className="form-label">Sale Date</label>
                <DateInput className="form-control" value={form.saleDate} onChange={e => set('saleDate', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Pond No</label>
                <select className="form-control" value={form.pondNo} onChange={e => set('pondNo', e.target.value)}>
                  {PONDS.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">DOC</label>
                <input className="form-control" type="number" value={form.doc} onChange={e => set('doc', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">UOM</label>
                <select className="form-control" value={form.uom} onChange={e => set('uom', e.target.value)}>
                  {['Kgs', 'Nos', 'Lsum'].map(u => <option key={u}>{u}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label className="form-label">Buyer *</label>
                <input className="form-control" placeholder="ASHVINI, RAVI_TPG…"
                  value={form.buyer} onChange={e => set('buyer', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Count (CNT)</label>
                <input className="form-control" placeholder="64 C, 65 C…" value={form.cnt} onChange={e => set('cnt', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">NOU (Weight Kg)</label>
                <input className="form-control" type="number" step="0.01" value={form.nou} onChange={e => set('nou', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">RPU (Rate/Kg)</label>
                <input className="form-control" type="number" step="0.01" value={form.rpu} onChange={e => set('rpu', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">CM (Commission)</label>
                <input className="form-control" type="number" step="0.01" value={form.cm} onChange={e => set('cm', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">T.RATE ← auto</label>
                <input className="form-control" type="number" step="0.01" value={form.tRate} onChange={e => set('tRate', e.target.value)}
                  style={{ fontWeight: 700, color: '#1a73e8' }} />
              </div>
              <div className="form-group">
                <label className="form-label">Amount (₹) ← auto</label>
                <input className="form-control" type="number" step="0.01" value={form.amount} onChange={e => set('amount', e.target.value)}
                  style={{ fontWeight: 700, color: '#137333' }} />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Notes</label>
                <input className="form-control" value={form.notes} onChange={e => set('notes', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button className="btn btn-primary" onClick={handleSave}>{editId ? 'Update' : 'Save'}</button>
              <button className="btn btn-secondary" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Summary banner */}
      {rows.length > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1, background: '#e8f0fe', borderRadius: 8, padding: '10px 16px', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 700, color: '#1a73e8' }}>TOTAL MATERIAL</span>
            <span style={{ fontWeight: 800, color: '#202124' }}>{totalKg.toFixed(3)} Kgs</span>
          </div>
          <div style={{ flex: 1, background: '#e6f4ea', borderRadius: 8, padding: '10px 16px', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 700, color: '#137333' }}>TOTAL AMOUNT</span>
            <span style={{ fontWeight: 800, color: '#137333' }}>₹{fmt(totalAmt)}</span>
          </div>
        </div>
      )}

      {loading ? <div className="loading-msg">Loading…</div> : (
        <div className="card">
          <div className="card-header"><div className="card-title">🦐 Harvests & Billing Summary</div></div>
          <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr style={{ background: '#1e3a5f', color: '#fff' }}>
                  {['DATE','P.No','DOC','BUYER','CNT','RPU','CM','UOM','NOU','T.RATE','AMOUNT (₹)','Actions'].map(h => (
                    <th key={h} style={{ color: '#fff', textAlign: ['RPU','CM','NOU','T.RATE','AMOUNT (₹)'].includes(h) ? 'right' : 'center' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={12} style={{ textAlign: 'center', color: '#888', padding: 32 }}>No sales recorded</td></tr>}
                {rows.map(r => (
                  <tr key={r.id}>
                    <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{fmtDate(r.sale_date)}</td>
                    <td style={{ textAlign: 'center', fontWeight: 700 }}>{r.pond_no}</td>
                    <td style={{ textAlign: 'center', fontWeight: 700 }}>{r.doc}</td>
                    <td style={{ fontWeight: 600 }}>{r.buyer}</td>
                    <td style={{ textAlign: 'center' }}>{r.cnt}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(r.rpu)}</td>
                    <td style={{ textAlign: 'right' }}>{r.cm > 0 ? fmt(r.cm) : '0'}</td>
                    <td style={{ textAlign: 'center' }}>{r.uom}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtQ(r.nou)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#1a73e8' }}>{fmt(r.t_rate)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 800, color: '#137333' }}>₹{fmt(r.amount)}</td>
                    <td>
                      {canWrite && <button className="btn-sm btn-secondary" onClick={() => handleEdit(r)} style={{ marginRight: 4 }}>Edit</button>}
                      {canDelete && <button className="btn-sm btn-danger" onClick={async () => { if (window.confirm('Delete?')) { await aquaApi.deleteHarvestSale(r.id); load(); } }}>Del</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr style={{ background: '#e8f0fe' }}>
                    <td colSpan={8} style={{ fontWeight: 800, textAlign: 'right', padding: '10px 12px', color: '#1e3a5f' }}>TOTAL</td>
                    <td style={{ textAlign: 'right', fontWeight: 800, padding: '10px 12px' }}>{totalKg.toFixed(3)} Kg</td>
                    <td></td>
                    <td style={{ textAlign: 'right', fontWeight: 800, padding: '10px 12px', color: '#137333', fontSize: 15 }}>₹{fmt(totalAmt)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Misc Income tab ──────────────────────────────────────────────────────────

const MISC_EMPTY = { cropId: '', incomeDate: new Date().toISOString().split('T')[0], description: '', uom: 'Nos', nou: '', rpu: '', amount: '', notes: '' };

function MiscIncomeTab({ cropId, activeCrops, canWrite, canDelete }) {
  const [rows, setRows]   = useState([]);
  const [form, setForm]   = useState({ ...MISC_EMPTY, cropId: cropId || '' });
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [err, setErr]   = useState('');

  const load = () => {
    if (!form.cropId && !cropId) return;
    setLoading(true);
    aquaApi.getHarvestMiscIncome(form.cropId || cropId).then(setRows).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [form.cropId, cropId]);

  const set = (k, v) => setForm(f => {
    const next = { ...f, [k]: v };
    if (k === 'nou' || k === 'rpu') {
      const nou = parseFloat(k === 'nou' ? v : next.nou) || 0;
      const rpu = parseFloat(k === 'rpu' ? v : next.rpu) || 0;
      if (next.uom !== 'Lsum') next.amount = (nou * rpu).toFixed(2);
    }
    return next;
  });

  const handleSave = async () => {
    if (!form.cropId) { setErr('Select a crop'); return; }
    if (!form.description.trim()) { setErr('Description is required'); return; }
    try {
      if (editId) await aquaApi.updateHarvestMiscIncome(editId, form);
      else await aquaApi.createHarvestMiscIncome(form);
      setForm({ ...MISC_EMPTY, cropId: form.cropId }); setEditId(null); setErr(''); setShowForm(false); load();
    } catch (e) { setErr(e.message); }
  };

  const handleEdit = (r) => {
    setForm({ cropId: r.crop_id, incomeDate: r.income_date || '', description: r.description || '',
      uom: r.uom || 'Nos', nou: r.nou || '', rpu: r.rpu || '', amount: r.amount || '', notes: r.notes || '' });
    setEditId(r.id); setShowForm(true);
  };

  const grandTotal = rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: '#5f6368' }}>🦐 Crop:</span>
          <select className="form-control" style={{ width: 180, padding: '4px 8px' }}
            value={form.cropId} onChange={e => setForm(f => ({ ...f, cropId: e.target.value }))}>
            <option value="">— Select Crop —</option>
            {activeCrops.slice().sort((a, b) => a.crop_code.localeCompare(b.crop_code)).map(c => <option key={c.id} value={c.id}>{c.crop_code}</option>)}
          </select>
        </div>
        {canWrite && (
          <button className="btn btn-primary" style={{ padding: '5px 14px', fontSize: 13 }}
            onClick={() => { setShowForm(s => !s); setEditId(null); setForm(f => ({ ...MISC_EMPTY, cropId: f.cropId })); }}>
            {showForm ? '✕ Close' : '➕ Add Income'}
          </button>
        )}
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><div className="card-title">{editId ? '✏️ Edit' : '➕ Add Misc Income'}</div></div>
          <div className="card-body">
            {err && <div className="alert alert-error" style={{ marginBottom: 10 }}>{err}</div>}
            <div className="form-grid cols-3">
              <div className="form-group">
                <label className="form-label">Date</label>
                <DateInput className="form-control" value={form.incomeDate} onChange={e => set('incomeDate', e.target.value)} />
              </div>
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label className="form-label">Description *</label>
                <input className="form-control" placeholder="SEED COVERS DISPOSAL, IRON SCRAP…"
                  value={form.description} onChange={e => set('description', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">UOM</label>
                <select className="form-control" value={form.uom} onChange={e => set('uom', e.target.value)}>
                  {UOMS.map(u => <option key={u}>{u}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">NOU</label>
                <input className="form-control" type="number" step="0.01" value={form.nou} onChange={e => set('nou', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">RPU (Rate)</label>
                <input className="form-control" type="number" step="0.01" value={form.rpu} onChange={e => set('rpu', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Amount (₹)</label>
                <input className="form-control" type="number" step="0.01" value={form.amount} onChange={e => set('amount', e.target.value)}
                  style={{ fontWeight: 700, color: '#137333' }} />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Notes</label>
                <input className="form-control" value={form.notes} onChange={e => set('notes', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button className="btn btn-primary" onClick={handleSave}>{editId ? 'Update' : 'Save'}</button>
              <button className="btn btn-secondary" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <div className="loading-msg">Loading…</div> : (
        <div className="card">
          <div className="card-header">
            <div className="card-title" style={{ color: '#7c3aed' }}>💰 Other… MISCELLANEOUs INCOME</div>
            <div style={{ fontWeight: 700, color: '#137333', fontSize: 15 }}>₹{fmt(grandTotal)}</div>
          </div>
          <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th><th>Description</th>
                  <th style={{ textAlign: 'center' }}>UOM</th>
                  <th style={{ textAlign: 'right' }}>NOU</th>
                  <th style={{ textAlign: 'right' }}>RPU</th>
                  <th style={{ textAlign: 'right' }}>Amount (₹)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888', padding: 32 }}>No misc income recorded</td></tr>}
                {rows.map(r => (
                  <tr key={r.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.income_date)}</td>
                    <td style={{ textAlign: 'center' }}>{r.description}</td>
                    <td style={{ textAlign: 'center' }}>{r.uom}</td>
                    <td style={{ textAlign: 'right' }}>{fmtQ(r.nou)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(r.rpu)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: '#137333' }}>₹{fmt(r.amount)}</td>
                    <td>
                      {canWrite && <button className="btn-sm btn-secondary" onClick={() => handleEdit(r)} style={{ marginRight: 4 }}>Edit</button>}
                      {canDelete && <button className="btn-sm btn-danger" onClick={async () => { if (window.confirm('Delete?')) { await aquaApi.deleteHarvestMiscIncome(r.id); load(); } }}>Del</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>TOTAL</td>
                    <td style={{ textAlign: 'right', fontWeight: 800, padding: '10px 12px', color: '#137333' }}>₹{fmt(grandTotal)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Receipts tab ──────────────────────────────────────────────────────────────

const RCPT_EMPTY = { cropId: '', receiptDate: new Date().toISOString().split('T')[0], rcvdOn: '', fromParty: '', toAccount: 'BRR', amount: '', notes: '' };

function ReceiptsTab({ cropId, activeCrops, canWrite, canDelete }) {
  const [rows, setRows]   = useState([]);
  const [form, setForm]   = useState({ ...RCPT_EMPTY, cropId: cropId || '' });
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [err, setErr]   = useState('');

  const load = () => {
    if (!form.cropId && !cropId) return;
    setLoading(true);
    aquaApi.getHarvestReceipts(form.cropId || cropId).then(setRows).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [form.cropId, cropId]);

  const handleSave = async () => {
    if (!form.cropId) { setErr('Select a crop'); return; }
    if (!form.amount) { setErr('Amount is required'); return; }
    try {
      if (editId) await aquaApi.updateHarvestReceipt(editId, form);
      else await aquaApi.createHarvestReceipt(form);
      setForm({ ...RCPT_EMPTY, cropId: form.cropId }); setEditId(null); setErr(''); setShowForm(false); load();
    } catch (e) { setErr(e.message); }
  };

  const handleEdit = (r) => {
    setForm({ cropId: r.crop_id, receiptDate: r.receipt_date || '', rcvdOn: r.rcvd_on || '',
      fromParty: r.from_party || '', toAccount: r.to_account || 'BRR', amount: r.amount || '', notes: r.notes || '' });
    setEditId(r.id); setShowForm(true);
  };

  const totalAmt    = rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);

  // running balance
  let running = 0;
  const rowsWithBal = rows.map(r => { running += parseFloat(r.amount || 0); return { ...r, _running: running }; });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: '#5f6368' }}>🦐 Crop:</span>
          <select className="form-control" style={{ width: 180, padding: '4px 8px' }}
            value={form.cropId} onChange={e => setForm(f => ({ ...f, cropId: e.target.value }))}>
            <option value="">— Select Crop —</option>
            {activeCrops.slice().sort((a, b) => a.crop_code.localeCompare(b.crop_code)).map(c => <option key={c.id} value={c.id}>{c.crop_code}</option>)}
          </select>
        </div>
        {canWrite && (
          <button className="btn btn-primary" style={{ padding: '5px 14px', fontSize: 13 }}
            onClick={() => { setShowForm(s => !s); setEditId(null); setForm(f => ({ ...RCPT_EMPTY, cropId: f.cropId })); }}>
            {showForm ? '✕ Close' : '➕ Add Receipt'}
          </button>
        )}
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><div className="card-title">{editId ? '✏️ Edit Receipt' : '➕ Add Payment Receipt'}</div></div>
          <div className="card-body">
            {err && <div className="alert alert-error" style={{ marginBottom: 10 }}>{err}</div>}
            <div className="form-grid cols-3">
              <div className="form-group">
                <label className="form-label">Receipt Date</label>
                <DateInput className="form-control" value={form.receiptDate} onChange={e => setForm(f => ({ ...f, receiptDate: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Received On (Bank Date)</label>
                <DateInput className="form-control" value={form.rcvdOn} onChange={e => setForm(f => ({ ...f, rcvdOn: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Amount (₹) *</label>
                <input className="form-control" type="number" step="0.01" value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  style={{ fontWeight: 700, color: '#137333' }} />
              </div>
              <div className="form-group">
                <label className="form-label">From (Buyer)</label>
                <input className="form-control" placeholder="ASHVINI, RAVI_TPG…"
                  value={form.fromParty} onChange={e => setForm(f => ({ ...f, fromParty: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">To (Account)</label>
                <input className="form-control" placeholder="BRR, SBI…"
                  value={form.toAccount} onChange={e => setForm(f => ({ ...f, toAccount: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <input className="form-control" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button className="btn btn-primary" onClick={handleSave}>{editId ? 'Update' : 'Save'}</button>
              <button className="btn btn-secondary" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <div className="loading-msg">Loading…</div> : (
        <div className="card">
          <div className="card-header">
            <div className="card-title">🧾 PAYMENT RECEIPTs</div>
            <div style={{ fontWeight: 700, color: '#137333', fontSize: 15 }}>Total Rcvd: ₹{fmt(totalAmt)}</div>
          </div>
          <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>DATE</th><th>AMOUNT</th><th>RCVD On</th>
                  <th>FROM</th><th>TO</th>
                  <th style={{ textAlign: 'right' }}>AMOUNT</th>
                  <th style={{ textAlign: 'right' }}>RUNNING TOTAL</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rowsWithBal.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', color: '#888', padding: 32 }}>No receipts recorded</td></tr>}
                {rowsWithBal.map(r => (
                  <tr key={r.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.receipt_date)}</td>
                    <td style={{ fontWeight: 600, color: '#137333' }}>₹{fmt(r.amount)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{r.rcvd_on}</td>
                    <td style={{ color: '#1a73e8', fontWeight: 500 }}>{r.from_party}</td>
                    <td>{r.to_account}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>₹{fmt(r.amount)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#1a73e8' }}>₹{fmt(r._running)}</td>
                    <td>
                      {canWrite && <button className="btn-sm btn-secondary" onClick={() => handleEdit(r)} style={{ marginRight: 4 }}>Edit</button>}
                      {canDelete && <button className="btn-sm btn-danger" onClick={async () => { if (window.confirm('Delete?')) { await aquaApi.deleteHarvestReceipt(r.id); load(); } }}>Del</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr style={{ background: '#e6f4ea' }}>
                    <td colSpan={5} style={{ fontWeight: 800, textAlign: 'right', padding: '10px 12px' }}>TOTAL RECEIPTS</td>
                    <td colSpan={2} style={{ textAlign: 'right', fontWeight: 800, padding: '10px 12px', color: '#137333', fontSize: 15 }}>₹{fmt(totalAmt)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Summary tab ───────────────────────────────────────────────────────────────

function SummaryTab({ cropId, activeCrops }) {
  const [selCrop, setSelCrop] = useState(cropId || '');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = (id) => {
    if (!id) return;
    setLoading(true);
    aquaApi.getHarvestSummary(id).then(setSummary).finally(() => setLoading(false));
  };
  useEffect(() => { load(selCrop || cropId); }, [selCrop, cropId]);

  const cropLabel = activeCrops.find(c => String(c.id) === String(selCrop || cropId))?.crop_code || '';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: '#5f6368' }}>🦐 Crop:</span>
        <select className="form-control" style={{ width: 200, padding: '4px 8px' }}
          value={selCrop} onChange={e => setSelCrop(e.target.value)}>
          <option value="">— Select Crop —</option>
          {activeCrops.slice().sort((a, b) => a.crop_code.localeCompare(b.crop_code)).map(c => <option key={c.id} value={c.id}>{c.crop_code}</option>)}
        </select>
      </div>

      {loading && <div className="loading-msg">Loading summary…</div>}

      {summary && (
        <div>
          {/* header */}
          <div style={{ background: 'linear-gradient(135deg,#1e3a5f,#0d47a1)', color: '#fff', borderRadius: 12, padding: '18px 24px', marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,.7)', fontWeight: 600, marginBottom: 4 }}>HARVEST SUMMARY</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>🦐 {cropLabel}</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,.8)', marginTop: 4 }}>
              Total Harvest: <strong>{parseFloat(summary.totalKg || 0).toFixed(3)} Kgs</strong>
            </div>
          </div>

          {/* income vs expense cards */}
          <div className="stats-grid" style={{ marginBottom: 20 }}>
            <SummaryCard label="Total Sale Income" value={summary.totalSaleAmt} icon="fa-fish" color="#137333" bg="#e6f4ea" />
            <SummaryCard label="Misc Income" value={summary.totalMisc} icon="fa-coins" color="#1a73e8" bg="#e8f0fe" />
            <SummaryCard label="Total Income" value={summary.totalIncome} icon="fa-chart-line" color="#0d47a1" bg="#c5cae9" sub="Sales + Misc" />
            <SummaryCard label="Harvest Expenses" value={summary.totalExpenses} icon="fa-receipt" color="#c5221f" bg="#fce8e6" />
          </div>

          {/* P&L + receipts */}
          <div className="aq-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div style={{ background: parseFloat(summary.netProfit) >= 0 ? '#e6f4ea' : '#fce8e6', borderRadius: 10, padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#5f6368', marginBottom: 6 }}>NET PROFIT (Income − Expenses)</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: parseFloat(summary.netProfit) >= 0 ? '#137333' : '#c5221f' }}>
                ₹{fmt(summary.netProfit)}
              </div>
            </div>
            <div style={{ background: '#e8f0fe', borderRadius: 10, padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#5f6368', marginBottom: 6 }}>PAYMENT BALANCE (Income − Received)</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: parseFloat(summary.balance) > 0 ? '#e37400' : '#137333' }}>
                ₹{fmt(summary.balance)}
              </div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Received: ₹{fmt(summary.totalReceipts)}</div>
            </div>
          </div>

          {/* breakdown table */}
          <div className="card">
            <div className="card-header"><div className="card-title">📋 Financial Breakdown</div></div>
            <div className="card-body" style={{ padding: 0 }}>
              <table className="data-table">
                <tbody>
                  {[
                    { label: 'Harvest Sale Amount', value: summary.totalSaleAmt, color: '#137333' },
                    { label: 'Miscellaneous Income', value: summary.totalMisc, color: '#1a73e8' },
                    { label: 'TOTAL INCOME', value: summary.totalIncome, color: '#0d47a1', bold: true },
                    { label: 'Harvest Expenses', value: summary.totalExpenses, color: '#c5221f' },
                    { label: 'NET PROFIT / (LOSS)', value: summary.netProfit, color: parseFloat(summary.netProfit) >= 0 ? '#137333' : '#c5221f', bold: true },
                    { label: 'Total Receipts Received', value: summary.totalReceipts, color: '#137333' },
                    { label: 'BALANCE PENDING', value: summary.balance, color: parseFloat(summary.balance) > 0 ? '#e37400' : '#137333', bold: true },
                  ].map((row, i) => (
                    <tr key={i} style={row.bold ? { background: '#f8f9fa' } : {}}>
                      <td style={{ fontWeight: row.bold ? 800 : 500, padding: '10px 16px', fontSize: row.bold ? 14 : 13 }}>{row.label}</td>
                      <td style={{ textAlign: 'right', fontWeight: row.bold ? 800 : 600, padding: '10px 16px', color: row.color, fontSize: row.bold ? 16 : 14 }}>
                        ₹{fmt(row.value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AquaHarvest({ cropId, cropCode, activeCrops = [], role = 'admin' }) {
  const canWrite = role !== 'readonly';
  const canDelete = role === 'admin';
  const [tab, setTab] = useState('summary');

  return (
    <div>
      <div className="tabs">
        <button className={`tab${tab === 'summary'  ? ' active' : ''}`} onClick={() => setTab('summary')}>📊 Summary</button>
        <button className={`tab${tab === 'expenses' ? ' active' : ''}`} onClick={() => setTab('expenses')}>💸 Expenses</button>
        <button className={`tab${tab === 'sales'    ? ' active' : ''}`} onClick={() => setTab('sales')}>🦐 Sales</button>
        <button className={`tab${tab === 'misc'     ? ' active' : ''}`} onClick={() => setTab('misc')}>💰 Misc Income</button>
        <button className={`tab${tab === 'receipts' ? ' active' : ''}`} onClick={() => setTab('receipts')}>🧾 Receipts</button>
      </div>

      <div style={{ marginTop: 16 }}>
        {tab === 'summary'  && <SummaryTab  cropId={cropId} activeCrops={activeCrops} />}
        {tab === 'expenses' && <ExpensesTab cropId={cropId} activeCrops={activeCrops} canWrite={canWrite} canDelete={canDelete} />}
        {tab === 'sales'    && <SalesTab    cropId={cropId} activeCrops={activeCrops} canWrite={canWrite} canDelete={canDelete} />}
        {tab === 'misc'     && <MiscIncomeTab cropId={cropId} activeCrops={activeCrops} canWrite={canWrite} canDelete={canDelete} />}
        {tab === 'receipts' && <ReceiptsTab cropId={cropId} activeCrops={activeCrops} canWrite={canWrite} canDelete={canDelete} />}
      </div>
    </div>
  );
}
